import { normalizeCrmActionHref } from "./safe-action-hrefs";

export type CrmContactAssessmentTone = "neutral" | "info" | "success" | "warning" | "danger";

export interface CrmContactAssessmentMetric {
  key: string;
  label: string;
  value: string;
  description: string | null;
  tone: CrmContactAssessmentTone;
}

export interface CrmContactAssessmentFinding {
  key: string;
  label: string;
  value: string | null;
  description: string | null;
  category: string | null;
  tone: CrmContactAssessmentTone;
}

export interface CrmContactAssessmentResult {
  key: string;
  label: string;
  assessmentType: string | null;
  completedAt: string | null;
  score: string | null;
  level: string | null;
  outcome: string | null;
  summary: string | null;
  href: string | null;
  actionLabel: string | null;
  tone: CrmContactAssessmentTone;
  findings: CrmContactAssessmentFinding[];
  metrics: CrmContactAssessmentMetric[];
}

export interface BuildCrmContactAssessmentResultsOptions {
  resultKeys?: readonly string[];
  maxResults?: number;
  maxMetricsPerResult?: number;
  maxFindingsPerResult?: number;
}

export const CRM_CONTACT_ASSESSMENT_RESULT_FIELD_KEYS = [
  "crmAssessmentResults",
  "contactAssessmentResults",
  "assessmentResults",
  "testResults",
  "diagnosticResults",
] as const;

const PRIVATE_KEY_PATTERN = /(?:password|secret|token|authorization|credential|private[_-]?key|api[_-]?key)/i;
const MAX_TEXT_LENGTH = 180;
const ASSESSMENT_FINDING_FIELD_KEYS = [
  ["findings", "Finding"],
  ["insights", "Insight"],
  ["highlights", "Highlight"],
  ["weaknesses", "Weakness"],
  ["topWeaknesses", "Weakness"],
  ["strengths", "Strength"],
  ["recommendations", "Recommendation"],
  ["nextSteps", "Next step"],
  ["flags", "Flag"],
] as const;

export function buildCrmContactAssessmentResults(
  customFields: unknown,
  options: BuildCrmContactAssessmentResultsOptions = {},
): CrmContactAssessmentResult[] {
  const fields = asRecord(customFields);
  if (!fields) return [];

  const resultKeys = options.resultKeys?.length
    ? [...options.resultKeys]
    : [...CRM_CONTACT_ASSESSMENT_RESULT_FIELD_KEYS];
  const maxResults = normalizePositiveInteger(options.maxResults, 8);
  const maxMetricsPerResult = normalizePositiveInteger(options.maxMetricsPerResult, 12);
  const maxFindingsPerResult = normalizePositiveInteger(options.maxFindingsPerResult, 8);

  return resultKeys
    .flatMap((key) => parseAssessmentResults(key, fields[key], maxMetricsPerResult, maxFindingsPerResult))
    .filter((result): result is CrmContactAssessmentResult => Boolean(result))
    .sort(compareAssessmentResults)
    .slice(0, maxResults);
}

function parseAssessmentResults(
  rootKey: string,
  value: unknown,
  maxMetricsPerResult: number,
  maxFindingsPerResult: number,
): Array<CrmContactAssessmentResult | null> {
  if (Array.isArray(value)) {
    return value.map((entry, index) => parseAssessmentResult(`${rootKey}.${index}`, entry, maxMetricsPerResult, maxFindingsPerResult));
  }

  const record = asRecord(value);
  if (!record) return [];

  if (hasAssessmentShape(record)) {
    return [parseAssessmentResult(rootKey, record, maxMetricsPerResult, maxFindingsPerResult)];
  }

  return Object.entries(record).map(([key, entry]) => parseAssessmentResult(key, entry, maxMetricsPerResult, maxFindingsPerResult));
}

function parseAssessmentResult(
  fallbackKey: string,
  value: unknown,
  maxMetricsPerResult: number,
  maxFindingsPerResult: number,
): CrmContactAssessmentResult | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  if (!record) return null;

  const key = readString(record.key) ?? fallbackKey;
  if (isPrivateKey(key)) return null;

  const label = truncate(
    readString(record.label)
    ?? readString(record.title)
    ?? readString(record.name)
    ?? formatLabel(key),
  ) ?? formatLabel(key);
  const assessmentType = truncate(readString(record.assessmentType) ?? readString(record.type) ?? readString(record.category));
  const completedAt = normalizeTimestamp(
    readString(record.completedAt)
    ?? readString(record.occurredAt)
    ?? readString(record.createdAt)
    ?? readString(record.timestamp)
    ?? readString(record.at),
  );
  const score = formatAssessmentValue(record.score ?? record.accuracy ?? record.percent ?? null);
  const level = truncate(readString(record.level) ?? readString(record.cefrLevel) ?? readString(record.placementLevel));
  const outcome = truncate(readString(record.outcome) ?? readString(record.status) ?? readString(record.result));
  const summary = truncate(readString(record.summary) ?? readString(record.description) ?? readString(record.evidence));
  const href = normalizeCrmActionHref(readString(record.href) ?? readString(record.url) ?? readString(record.to));
  const actionLabel = truncate(
    readString(record.actionLabel)
    ?? readString(record.ctaLabel)
    ?? readString(record.linkLabel),
  );
  const metrics = parseAssessmentMetrics(record.metrics ?? record.items ?? record.fields ?? record.scores ?? record.signals ?? record, maxMetricsPerResult);
  const findings = parseAssessmentFindings(record, maxFindingsPerResult);

  if (!score && !level && !outcome && !summary && !href && metrics.length === 0 && findings.length === 0) return null;

  return {
    key,
    label,
    assessmentType,
    completedAt,
    score,
    level,
    outcome,
    summary,
    href,
    actionLabel,
    tone: normalizeTone(readString(record.tone) ?? readString(record.status) ?? readString(record.outcome)),
    findings,
    metrics,
  };
}

function parseAssessmentFindings(
  record: Record<string, unknown>,
  maxFindings: number,
): CrmContactAssessmentFinding[] {
  const findings: CrmContactAssessmentFinding[] = [];

  for (const [fieldKey, category] of ASSESSMENT_FINDING_FIELD_KEYS) {
    if (!(fieldKey in record)) continue;
    findings.push(...parseAssessmentFindingEntries(fieldKey, record[fieldKey], category)
      .filter((finding): finding is CrmContactAssessmentFinding => Boolean(finding)));
    if (findings.length >= maxFindings) break;
  }

  return findings.slice(0, maxFindings);
}

function parseAssessmentFindingEntries(
  rootKey: string,
  value: unknown,
  category: string,
): Array<CrmContactAssessmentFinding | null> {
  if (Array.isArray(value)) {
    return value.map((entry, index) => parseAssessmentFinding(`${rootKey}.${index}`, entry, category));
  }

  const record = asRecord(value);
  if (!record) return [parseAssessmentFinding(rootKey, value, category)];

  if (hasAssessmentFindingShape(record)) {
    return [parseAssessmentFinding(rootKey, record, category)];
  }

  return Object.entries(record).map(([key, entry]) => parseAssessmentFinding(key, entry, category));
}

function parseAssessmentFinding(
  fallbackKey: string,
  value: unknown,
  fallbackCategory: string,
): CrmContactAssessmentFinding | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  const key = record ? readString(record.key) ?? fallbackKey : fallbackKey;
  if (isPrivateKey(key)) return null;

  if (!record) {
    const label = formatAssessmentValue(value);
    if (!label) return null;
    return {
      key,
      label,
      value: null,
      description: null,
      category: fallbackCategory,
      tone: "neutral",
    };
  }

  const rawValue = "value" in record ? formatAssessmentValue(record.value) : null;
  const label = truncate(
    readString(record.label)
    ?? readString(record.title)
    ?? readString(record.name)
    ?? readString(record.summary)
    ?? rawValue
    ?? formatLabel(key),
  ) ?? formatLabel(key);
  const valueText = rawValue && rawValue !== label ? rawValue : null;
  const description = truncate(
    readString(record.description)
    ?? readString(record.detail)
    ?? readString(record.evidence)
    ?? readString(record.helpText),
  );
  const category = truncate(
    readString(record.category)
    ?? readString(record.type)
    ?? readString(record.kind),
  ) ?? fallbackCategory;

  if (!label && !valueText && !description) return null;

  return {
    key,
    label,
    value: valueText,
    description,
    category,
    tone: normalizeTone(
      readString(record.tone)
      ?? readString(record.status)
      ?? readString(record.priority)
      ?? readString(record.outcome),
    ),
  };
}

function parseAssessmentMetrics(value: unknown, maxMetrics: number): CrmContactAssessmentMetric[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parseAssessmentMetric(`metric-${index + 1}`, entry))
      .filter((metric): metric is CrmContactAssessmentMetric => Boolean(metric))
      .slice(0, maxMetrics);
  }

  const record = asRecord(value);
  if (!record) return [];

  const ignoredResultKeys = new Set([
    "key",
    "label",
    "title",
    "name",
    "assessmentType",
    "type",
    "category",
    "completedAt",
    "occurredAt",
    "createdAt",
    "timestamp",
    "at",
    "score",
    "accuracy",
    "percent",
    "level",
    "cefrLevel",
    "placementLevel",
    "outcome",
    "status",
    "result",
    "summary",
    "description",
    "evidence",
    "href",
    "url",
    "to",
    "actionLabel",
    "ctaLabel",
    "linkLabel",
    ...ASSESSMENT_FINDING_FIELD_KEYS.map(([key]) => key),
    "tone",
    "metrics",
    "items",
    "fields",
    "scores",
    "signals",
  ]);

  return Object.entries(record)
    .filter(([key]) => !ignoredResultKeys.has(key))
    .map(([key, entry]) => parseAssessmentMetric(key, entry))
    .filter((metric): metric is CrmContactAssessmentMetric => Boolean(metric))
    .slice(0, maxMetrics);
}

function parseAssessmentMetric(fallbackKey: string, value: unknown): CrmContactAssessmentMetric | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  const key = record ? readString(record.key) ?? fallbackKey : fallbackKey;
  if (isPrivateKey(key)) return null;

  const label = record
    ? truncate(readString(record.label) ?? readString(record.title) ?? formatLabel(key)) ?? formatLabel(key)
    : formatLabel(key);
  const rawValue = record && "value" in record
    ? record.value
    : record?.score ?? record?.accuracy ?? record?.percent ?? record?.result ?? value;
  const formattedValue = formatAssessmentValue(rawValue);
  if (!formattedValue) return null;

  return {
    key,
    label,
    value: formattedValue,
    description: record ? truncate(readString(record.description) ?? readString(record.helpText)) : null,
    tone: normalizeTone(record ? readString(record.tone) ?? readString(record.status) : null),
  };
}

function hasAssessmentShape(record: Record<string, unknown>): boolean {
  return [
    "label",
    "title",
    "name",
    "assessmentType",
    "completedAt",
    "score",
    "accuracy",
    "percent",
    "level",
    "cefrLevel",
    "placementLevel",
    "outcome",
    "summary",
    "description",
    "href",
    "url",
    "to",
    "actionLabel",
    "ctaLabel",
    "linkLabel",
    ...ASSESSMENT_FINDING_FIELD_KEYS.map(([key]) => key),
    "metrics",
    "items",
    "fields",
    "scores",
    "signals",
  ].some((key) => key in record);
}

function hasAssessmentFindingShape(record: Record<string, unknown>): boolean {
  return [
    "key",
    "label",
    "title",
    "name",
    "value",
    "summary",
    "description",
    "detail",
    "evidence",
    "helpText",
    "category",
    "type",
    "kind",
    "tone",
    "status",
    "priority",
    "outcome",
  ].some((key) => key in record);
}

function compareAssessmentResults(
  left: CrmContactAssessmentResult,
  right: CrmContactAssessmentResult,
): number {
  const leftTime = toTime(left.completedAt);
  const rightTime = toTime(right.completedAt);
  if (leftTime === null && rightTime === null) return 0;
  if (leftTime === null) return 1;
  if (rightTime === null) return -1;
  return rightTime - leftTime;
}

function formatAssessmentValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return truncate(value.trim());
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    return value > 0 && value <= 1 ? `${Math.round(value * 100)}%` : String(value);
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    const primitiveValues = value
      .filter((entry) => ["string", "number", "boolean"].includes(typeof entry))
      .map((entry) => formatAssessmentValue(entry))
      .filter((entry): entry is string => Boolean(entry));
    if (primitiveValues.length === 0) return value.length > 0 ? `${value.length} records` : null;
    const preview = primitiveValues.slice(0, 4).join(", ");
    return value.length > 4 ? `${preview}, +${value.length - 4} more` : preview;
  }
  return null;
}

function normalizeTone(value: string | null): CrmContactAssessmentTone {
  if (value === "success" || value === "warning" || value === "danger" || value === "info") return value;
  if (value === "critical" || value === "error" || value === "failed" || value === "failure") return "danger";
  if (value === "ready" || value === "passed" || value === "complete" || value === "completed") return "success";
  if (value === "pending" || value === "at_risk" || value === "needs_review" || value === "needs_practice") return "warning";
  return "neutral";
}

function normalizeTimestamp(value: string | null): string | null {
  if (!value) return null;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return null;
  return new Date(timestamp).toISOString();
}

function toTime(value: string | null): number | null {
  if (!value) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function formatLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizePositiveInteger(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : fallback;
}

function truncate(value: string | null): string | null {
  if (!value) return null;
  return value.length > MAX_TEXT_LENGTH ? `${value.slice(0, MAX_TEXT_LENGTH - 3)}...` : value;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function isPrivateKey(key: string): boolean {
  return PRIVATE_KEY_PATTERN.test(key);
}
